import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  effect,
  signal,
  inject,
} from '@angular/core';
import { FeatureFlagStore } from '@services/feature-flags/feature-flag.store';
import { DashboardService } from '@services/http/dashboard.service';
import {
  DashboardEconomyScenarioResponse,
  DashboardEconomySimulationResponse,
} from '@interfaces/dashboard/responses/dashboard-economy-scenario.response';
import {
  EconomyBenchmarkSimulation,
  EconomyPeriodSelection,
  EconomyScenarioSimulationService,
} from '@features/dashboard/dashboard-management/components/economy-scenario-simulation.service';
import { DashboardEconomyHistoryHttpService } from '@services/http/dashboard/dashboard-economy-history-http.service';
import { DashboardEconomyAsOfTotals } from '@interfaces/dashboard/responses/dashboard-economy-as-of.response';
import {
  EconomyAsOfSum,
  EconomyPeriodDelta,
  economyPeriodDelta,
  sumEconomyAsOf,
} from './economy-period.util';
import { DateFormatterService } from '@core/services/helpers/date-formatter.service';
import { VaultFormatCurrencyPipe } from '@shared/pipes/vault-format-currency.pipe';
import { TooltipModule } from 'primeng/tooltip';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  distinctUntilChanged,
  from,
  map,
  merge,
  mergeMap,
  of,
  switchMap,
  toArray,
  take,
  takeUntil,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { EconomyOperationsModalComponent } from './economy-operations-modal/economy-operations-modal.component';
import { SharedModule } from '@shared/shared.module';

@Component({
  selector: 'app-economy-scenario-cards',
  standalone: true,
  imports: [
    CommonModule,
    VaultFormatCurrencyPipe,
    TooltipModule,
    EconomyOperationsModalComponent,
    SharedModule,
  ],
  templateUrl: './economy-scenario-cards.component.html',
  styleUrls: ['./economy-scenario-cards.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EconomyScenarioCardsComponent implements OnChanges, OnDestroy {
  /**
   * Re-consulta enquanto o backend responder `status: 'pending'` (snapshot sendo gerado em
   * background). Sem isso o card ficava preso em "Cenário sendo calculado…" até um F5 manual.
   * Intervalo acima do TTL do cache do DashboardService; tentativas limitadas (~15 min).
   */
  private static readonly PENDING_POLL_INTERVAL_MS = 45_000;
  private static readonly PENDING_POLL_MAX_ATTEMPTS = 20;

  @Input() selectedCompanyIds: string[] = [];

  /**
   * Home (Task 3839): ao lado do Calendário de Vencimentos o bloco estica até a altura dele e os dois cards dividem
   * essa altura ao meio (estado de um quadro só, como pendente e erro, ocupa a altura inteira). Sozinho na linha ou em
   * outra tela, fica com a altura natural.
   */
  @Input() fillHeight = false;

  @ViewChild('operationsModal') operationsModal: EconomyOperationsModalComponent | null = null;

  readonly data = signal<DashboardEconomyScenarioResponse | null>(null);

  /**
   * Flag dashboard-economy-history: libera o botão do histórico na modal de operações (card 3295).
   * Reconsultada ao voltar para a aba e ao navegar: liberou, aparece sem relogar (card 3323).
   */
  readonly canSeeEconomyHistory = inject(FeatureFlagStore).watch('dashboard-economy-history');
  readonly isLoading = signal(false);
  readonly hasError = signal(false);

  /** Resultado da simulação de benchmark aplicada no gráfico (null = benchmark oficial). */
  readonly simulationData = signal<DashboardEconomySimulationResponse | null>(null);

  /** Simulação em andamento (skeleton nos valores dos cards). */
  readonly isSimulating = signal(false);

  /** Falha na última simulação — cards permanecem no benchmark oficial. */
  readonly simulationError = signal(false);

  /** Período do gráfico aplicado aos cards (card 3323): economia na data final e no período. */
  readonly periodData = signal<EconomyPeriodView | null>(null);

  /** Cálculo da economia na data passada em andamento (o backend leva alguns segundos por empresa). */
  readonly isLoadingPeriod = signal(false);

  /** Progresso do cálculo: chamadas concluídas e total (empresas × datas). */
  readonly periodProgress = signal<{ done: number; total: number }>({ done: 0, total: 0 });

  /** Falha ao calcular a economia na data: cards voltam a mostrar hoje. */
  readonly periodError = signal(false);

  /**
   * Resultados já buscados nesta sessão, por empresa e data. Só guarda data anterior a hoje e
   * resultado completo: hoje e cálculo parcial podem mudar e são buscados de novo.
   */
  private readonly asOfCache = new Map<string, EconomyAsOfResult>();

  private destroy$ = new Subject<void>();
  private readonly selectedCompanyIds$ = new Subject<string[]>();
  /** "Tentar de novo" do erro: relê as mesmas empresas (passa por fora do `distinctUntilChanged`). */
  private readonly retry$ = new Subject<void>();
  private readonly simulationParams$ = new Subject<EconomyBenchmarkSimulation | null>();
  private readonly periodParams$ = new Subject<EconomyPeriodSelection | null>();
  private readonly historyService = inject(DashboardEconomyHistoryHttpService);
  private readonly dateFormatter = inject(DateFormatterService);

  constructor(
    private dashboardService: DashboardService,
    private readonly simulationState: EconomyScenarioSimulationService
  ) {
    // Período aplicado no gráfico: aplicações seguidas cancelam a anterior (switchMap).
    effect(() => this.periodParams$.next(this.simulationState.period()));
    this.periodParams$
      .pipe(
        switchMap(period => this.loadPeriod(period)),
        takeUntil(this.destroy$)
      )
      .subscribe(view => {
        this.periodData.set(view);
        this.isLoadingPeriod.set(false);
      });

    merge(
      this.selectedCompanyIds$.pipe(
        distinctUntilChanged(
          (prev, curr) => this.normalizeCompanyIds(prev) === this.normalizeCompanyIds(curr)
        )
      ),
      this.retry$.pipe(map(() => this.selectedCompanyIds ?? []))
    )
      .pipe(
        tap(companyIds => this.prepareLoadingState(companyIds)),
        switchMap(companyIds => {
          if (!companyIds.length) return EMPTY;

          // Enquanto a resposta for 'pending', repete a leitura até o snapshot ficar pronto
          // (takeWhile inclusivo emite também a primeira resposta não-pendente). Troca de
          // empresa cancela o polling pelo switchMap externo.
          return timer(0, EconomyScenarioCardsComponent.PENDING_POLL_INTERVAL_MS).pipe(
            switchMap(() => this.dashboardService.getEconomyScenario(companyIds)),
            takeWhile(res => res?.status === 'pending', true),
            take(EconomyScenarioCardsComponent.PENDING_POLL_MAX_ATTEMPTS),
            catchError(() => {
              this.hasError.set(true);
              this.isLoading.set(false);
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(res => {
        this.data.set(res);
        this.isLoading.set(false);
      });

    // Ponte sinal → RxJS: a simulação publicada pelo gráfico entra num switchMap para que
    // aplicações consecutivas cancelem a requisição anterior (mesmo padrão do snapshot).
    effect(() => this.simulationParams$.next(this.simulationState.simulation()));

    this.simulationParams$
      .pipe(
        switchMap(params => {
          this.simulationError.set(false);
          if (!params || !this.selectedCompanyIds?.length) {
            this.simulationData.set(null);
            this.isSimulating.set(false);
            return EMPTY;
          }

          this.isSimulating.set(true);
          return this.dashboardService
            .getEconomyScenarioSimulation(this.selectedCompanyIds, params.indexer, params.spread)
            .pipe(
              catchError(() => {
                // Falha na simulação: mantém o benchmark oficial visível e sinaliza o erro.
                this.simulationData.set(null);
                this.isSimulating.set(false);
                this.simulationError.set(true);
                return EMPTY;
              })
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(res => {
        this.simulationData.set(res);
        this.isSimulating.set(false);
      });
  }

  /** Cards voltam para hoje; o gráfico volta a mostrar a curva inteira. */
  resetPeriod(): void {
    this.simulationState.requestPeriodReset();
  }

  /**
   * Busca a economia das empresas na data final e, se houver, na inicial. Uma empresa por chamada
   * (limite do backend), duas em paralelo. Falha mostra o erro e mantém os cards de hoje.
   */
  private loadPeriod(period: EconomyPeriodSelection | null): Observable<EconomyPeriodView | null> {
    this.periodError.set(false);
    const companyIds = [...(this.selectedCompanyIds ?? [])].filter(Boolean);
    if (!period || !companyIds.length) {
      this.isLoadingPeriod.set(false);
      return of(null);
    }

    const dates = period.from ? [period.to, period.from] : [period.to];
    const calls = dates.flatMap(date => companyIds.map(companyId => ({ companyId, date })));
    this.periodProgress.set({ done: 0, total: calls.length });
    this.isLoadingPeriod.set(true);

    return from(calls).pipe(
      mergeMap(
        call =>
          this.fetchAsOf(call.companyId, call.date).pipe(map(result => ({ ...call, ...result }))),
        2
      ),
      tap(() => this.periodProgress.update(p => ({ ...p, done: p.done + 1 }))),
      toArray(),
      map(results => {
        const sumAt = (date: string): EconomyAsOfSum =>
          sumEconomyAsOf(results.filter(r => r.date === date).map(r => r.totals));
        const end = sumAt(period.to);
        const start = period.from ? sumAt(period.from) : null;
        const partial = results.some(r => r.partial);
        return { period, end, start, delta: economyPeriodDelta(end, start), partial };
      }),
      catchError(() => {
        this.periodError.set(true);
        this.isLoadingPeriod.set(false);
        return of(null);
      })
    );
  }

  private fetchAsOf(companyId: string, date: string): Observable<EconomyAsOfResult> {
    const key = `${companyId}|${date}`;
    const cached = this.asOfCache.get(key);
    if (cached) return of(cached);
    const today = this.dateFormatter.formatDateToYYYYMMDD(this.dateFormatter.getTodayDate());
    return this.historyService.getEconomyAsOf(companyId, date).pipe(
      map(res => ({
        totals: res?.totals ?? null,
        partial: !!res?.companies?.some(c => c.status === 'partial'),
      })),
      tap(result => {
        if (!result.partial && date < today) this.asOfCache.set(key, result);
      })
    );
  }

  /** Volta os cards (e o gráfico, via serviço compartilhado) ao benchmark oficial da empresa. */
  resetSimulation(): void {
    this.simulationState.requestReset();
  }

  get hasPerOperation(): boolean {
    return !!this.data()?.perOperation?.length;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCompanyIds']) {
      this.selectedCompanyIds$.next(this.selectedCompanyIds ?? []);
    }
  }

  /** Relê o cenário depois de um erro, com as mesmas empresas. */
  retry(): void {
    this.retry$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openOperationsModal(): void {
    const currentData = this.data();
    if (!this.hasPerOperation || !currentData?.perOperation) {
      return;
    }
    this.operationsModal?.open(
      currentData.perOperation,
      currentData.benchmarkLabel ?? '',
      this.selectedCompanyIds,
      this.canSeeEconomyHistory()
    );
  }

  private prepareLoadingState(companyIds: string[]): void {
    // Troca de empresa: resultados de data passada eram das empresas anteriores.
    this.periodData.set(null);
    this.periodError.set(false);

    // Troca de empresa invalida qualquer simulação anterior (o gráfico também limpa o serviço).
    this.simulationData.set(null);
    this.isSimulating.set(false);
    this.simulationError.set(false);

    if (!companyIds.length) {
      this.data.set(null);
      this.isLoading.set(false);
      this.hasError.set(false);
      return;
    }

    this.data.set(null);
    this.isLoading.set(true);
    this.hasError.set(false);
  }

  private normalizeCompanyIds(companyIds: string[]): string {
    return [...(companyIds ?? [])].sort().join('|');
  }
}

/** Economia das empresas no período aplicado: na data final, na inicial e a diferença. */
export interface EconomyPeriodView {
  period: EconomyPeriodSelection;
  end: EconomyAsOfSum;
  start: EconomyAsOfSum | null;
  delta: EconomyPeriodDelta | null;
  /** Alguma empresa veio com operação fora da soma por falha de cálculo. */
  partial: boolean;
}

/** Economia de uma empresa numa data e se o backend deixou operação fora da soma. */
interface EconomyAsOfResult {
  totals: DashboardEconomyAsOfTotals | null;
  partial: boolean;
}
